// src/components/inventory/EditItemModal.tsx
// REFACTORED: Instance-specific fields only
// Product/Purchase info displayed as read-only summary, not editable
// Updated: Body armor section shows based on category config OR existing data

import React, { useState, useEffect } from 'react';
import { X, Save, Trash2, AlertCircle, Shield } from 'lucide-react';
import { InventoryItem as APIInventoryItem } from '../../services/inventoryAPI';
import PhotoUpload from '../ui/PhotoUpload';
import { tenantConfigAPI } from '../../services/tenantConfigAPI';
import { categoryAPI } from '../../services/categoryAPI';
import { requiresExpiration } from '../../utils/categoryTracking';
import { CategoryHierarchy } from '../../types/category';

interface EditItemModalProps {
  isOpen: boolean;
  item: APIInventoryItem | null;
  onClose: () => void;
  onSave: (itemId: string, itemData: any) => void;
  onDelete?: (itemId: string) => void;
}

const EditItemModal: React.FC<EditItemModalProps> = ({ isOpen, item, onClose, onSave, onDelete }) => {
  const [formData, setFormData] = useState({
    serialNumber: '',
    status: 'Available',
    condition: 'Excellent',
    location: '',
    subLocation: '',
    availableQuantity: '',
    minStockLevel: '',
    notes: '',
    photos: [] as string[],
    ballisticLevel: '',
    size: '',
    expirationDate: '',
    lastInspectionDate: '',
    nextInspectionDue: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showSerialConfirm, setShowSerialConfirm] = useState(false);
  const [originalSerialNumber, setOriginalSerialNumber] = useState('');
  const [locations, setLocations] = useState<string[]>([]);
  const [needsExpiration, setNeedsExpiration] = useState(false);

  const statusOptions = ['Available', 'Assigned', 'Maintenance', 'Retired', 'Damaged', 'Lost'];
  const conditionOptions = ['Excellent', 'Good', 'Fair', 'Poor', 'Needs Repair'];
  const ballisticLevels = ['IIA', 'II', 'IIIA', 'III', 'IV'];
  const sizes = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL'];

  useEffect(() => {
    if (isOpen) {
      tenantConfigAPI.getInventoryLocations().then(locs => {
        setLocations(locs?.length ? locs : ['Armory', 'Equipment Room', 'Supply Room', 'Vehicle Bay', 'Storage Unit']);
      }).catch(() => setLocations(['Armory', 'Equipment Room', 'Supply Room', 'Vehicle Bay', 'Storage Unit']));
    }
  }, [isOpen]);

  // Check if category requires expiration OR item already has body armor data
  useEffect(() => {
    const checkExpiration = async () => {
      if (!item) return;

      // Check if item already has valid body armor data
      const ba = (item as any).bodyArmor;
      const hasExistingData = ba && typeof ba === 'object' && !ba.NULL && 
        (ba.ballisticLevel || ba.size || ba.expirationDate);
      
      if (hasExistingData) {
        setNeedsExpiration(true);
        return;
      }

      // Check category configuration
      const categoryId = (item as any).subcategoryId || (item as any).categoryId;
      if (!categoryId) return;

      try {
        const response = await categoryAPI.getAllCategories();
        if (response.success && response.data) {
          const category = response.data.categories.find(c => c.categoryId === categoryId);
          if (category) {
            setNeedsExpiration(requiresExpiration(category as CategoryHierarchy));
          }
        }
      } catch (err) {
        console.error('Failed to check category expiration:', err);
      }
    };

    if (isOpen && item) {
      setNeedsExpiration(false);
      checkExpiration();
    }
  }, [isOpen, item]);

  useEffect(() => {
    if (isOpen && item) {
      const serial = item.itemType === 'SERIALIZED' ? ((item as any).serialNumber || '') : '';
      setOriginalSerialNumber(serial);
      const ba = (item as any).bodyArmor;
      const validBa = ba && typeof ba === 'object' && !ba.NULL;
      
      setFormData({
        serialNumber: serial,
        status: (item as any).status || 'Available',
        condition: item.condition || 'Excellent',
        location: item.location || '',
        subLocation: item.subLocation || '',
        availableQuantity: item.itemType !== 'SERIALIZED' ? ((item as any).availableQuantity?.toString() || '') : '',
        minStockLevel: item.itemType !== 'SERIALIZED' ? ((item as any).minStockLevel?.toString() || '') : '',
        notes: item.description || '',
        photos: item.photos || [],
        ballisticLevel: validBa ? (ba.ballisticLevel || '') : '',
        size: validBa ? (ba.size || '') : '',
        expirationDate: validBa ? (ba.expirationDate?.split('T')[0] || '') : '',
        lastInspectionDate: validBa ? (ba.lastInspectionDate?.split('T')[0] || '') : '',
        nextInspectionDue: validBa ? (ba.nextInspectionDue?.split('T')[0] || '') : ''
      });
      setErrors({});
      setShowDeleteConfirm(false);
      setShowSerialConfirm(false);
    }
  }, [isOpen, item]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(p => ({ ...p, [name]: value }));
    if (errors[name]) setErrors(p => ({ ...p, [name]: '' }));
  };

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!formData.location) errs.location = 'Required';
    if (item?.itemType === 'SERIALIZED' && !formData.serialNumber.trim()) errs.serialNumber = 'Required';
    if (item?.itemType !== 'SERIALIZED' && (!formData.availableQuantity || parseInt(formData.availableQuantity) < 0)) {
      errs.availableQuantity = 'Must be 0 or greater';
    }
    if (needsExpiration) {
      if (!formData.ballisticLevel) errs.ballisticLevel = 'Required';
      if (!formData.size) errs.size = 'Required';
      if (!formData.expirationDate) errs.expirationDate = 'Required';
    }
    setErrors(errs);
    return !Object.keys(errs).length;
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!item || !validate()) return;
    if (item.itemType === 'SERIALIZED' && formData.serialNumber !== originalSerialNumber && !showSerialConfirm) {
      setShowSerialConfirm(true);
      return;
    }
    setSaving(true);
    try {
      const data: any = {
        location: formData.location,
        subLocation: formData.subLocation || undefined,
        condition: formData.condition,
        description: formData.notes || undefined,
        photos: formData.photos
      };
      if (item.itemType === 'SERIALIZED') {
        data.serialNumber = formData.serialNumber;
        data.status = formData.status;
      } else {
        data.availableQuantity = parseInt(formData.availableQuantity);
        if (formData.minStockLevel) data.minStockLevel = parseInt(formData.minStockLevel);
      }
      if (needsExpiration) {
        data.bodyArmor = {
          ballisticLevel: formData.ballisticLevel,
          size: formData.size,
          expirationDate: formData.expirationDate,
          lastInspectionDate: formData.lastInspectionDate || undefined,
          nextInspectionDue: formData.nextInspectionDue || undefined
        };
      }
      await onSave(item.itemId, data);
      setShowSerialConfirm(false);
    } catch (err) {
      console.error('Save error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!item || !onDelete) return;
    setDeleting(true);
    try {
      await onDelete(item.itemId);
    } catch (err) {
      console.error('Delete error:', err);
    } finally {
      setDeleting(false);
    }
  };

  if (!isOpen || !item) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b bg-gray-50">
          <div>
            <h2 className="text-lg font-bold text-gray-800">Edit: {item.name}</h2>
            <p className="text-xs text-gray-500">{item.manufacturer} {item.model} • {(item as any).category || 'Uncategorized'}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-lg"><X className="w-5 h-5" /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Serialized fields */}
          {item.itemType === 'SERIALIZED' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Serial Number *</label>
                <input name="serialNumber" value={formData.serialNumber} onChange={handleChange} className={`w-full px-3 py-2 border rounded-lg ${errors.serialNumber ? 'border-red-500' : 'border-gray-300'}`} />
                {formData.serialNumber !== originalSerialNumber && <p className="text-xs text-amber-600 mt-1 flex items-center"><AlertCircle className="w-3 h-3 mr-1" />Will update official record</p>}
                {errors.serialNumber && <p className="text-red-500 text-xs mt-1">{errors.serialNumber}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Status</label>
                <select name="status" value={formData.status} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                  {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
          )}

          {/* Bulk fields */}
          {item.itemType !== 'SERIALIZED' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Available Qty *</label>
                <input type="number" name="availableQuantity" value={formData.availableQuantity} onChange={handleChange} min="0" className={`w-full px-3 py-2 border rounded-lg ${errors.availableQuantity ? 'border-red-500' : 'border-gray-300'}`} />
                {errors.availableQuantity && <p className="text-red-500 text-xs mt-1">{errors.availableQuantity}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Min Stock Level</label>
                <input type="number" name="minStockLevel" value={formData.minStockLevel} onChange={handleChange} min="0" className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
            </div>
          )}

          {/* Common fields */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Location *</label>
              <select name="location" value={formData.location} onChange={handleChange} className={`w-full px-3 py-2 border rounded-lg ${errors.location ? 'border-red-500' : 'border-gray-300'}`}>
                <option value="">Select...</option>
                {locations.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
              {errors.location && <p className="text-red-500 text-xs mt-1">{errors.location}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Sub-Location</label>
              <input name="subLocation" value={formData.subLocation} onChange={handleChange} placeholder="Shelf, Bin, etc." className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Condition</label>
            <select name="condition" value={formData.condition} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
              {conditionOptions.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Notes</label>
            <textarea name="notes" value={formData.notes} onChange={handleChange} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
          </div>

          {/* Body Armor */}
          {needsExpiration && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <h4 className="font-medium text-amber-900 mb-3 flex items-center">
                <Shield className="w-4 h-4 mr-2" />
                Body Armor / Expiration Tracking
              </h4>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1">Ballistic Level *</label>
                  <select name="ballisticLevel" value={formData.ballisticLevel} onChange={handleChange} className={`w-full px-2 py-1 text-sm border rounded ${errors.ballisticLevel ? 'border-red-500' : 'border-gray-300'}`}>
                    <option value="">Select</option>
                    {ballisticLevels.map(l => <option key={l} value={l}>{l}</option>)}
                  </select>
                  {errors.ballisticLevel && <p className="text-red-500 text-xs mt-1">{errors.ballisticLevel}</p>}
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Size *</label>
                  <select name="size" value={formData.size} onChange={handleChange} className={`w-full px-2 py-1 text-sm border rounded ${errors.size ? 'border-red-500' : 'border-gray-300'}`}>
                    <option value="">Select</option>
                    {sizes.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  {errors.size && <p className="text-red-500 text-xs mt-1">{errors.size}</p>}
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Expiration *</label>
                  <input type="date" name="expirationDate" value={formData.expirationDate} onChange={handleChange} className={`w-full px-2 py-1 text-sm border rounded ${errors.expirationDate ? 'border-red-500' : 'border-gray-300'}`} />
                  {errors.expirationDate && <p className="text-red-500 text-xs mt-1">{errors.expirationDate}</p>}
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Last Inspection</label>
                  <input type="date" name="lastInspectionDate" value={formData.lastInspectionDate} onChange={handleChange} className="w-full px-2 py-1 text-sm border border-gray-300 rounded" />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Next Due</label>
                  <input type="date" name="nextInspectionDue" value={formData.nextInspectionDue} onChange={handleChange} className="w-full px-2 py-1 text-sm border border-gray-300 rounded" />
                </div>
              </div>
            </div>
          )}

          {/* Photos */}
          <div>
            <label className="block text-sm font-medium mb-2">Photos</label>
            <PhotoUpload itemId={item.itemId} photos={formData.photos} onPhotosUpdate={(p) => setFormData(f => ({ ...f, photos: p }))} maxPhotos={5} />
          </div>

          {/* Actions */}
          <div className="flex justify-between pt-4 border-t">
            {onDelete && <button type="button" onClick={() => setShowDeleteConfirm(true)} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center"><Trash2 className="w-4 h-4 mr-1" />Delete</button>}
            <div className="flex gap-2 ml-auto">
              <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
              <button type="submit" disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center">
                {saving ? <><div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />Saving...</> : <><Save className="w-4 h-4 mr-1" />Save</>}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Serial Confirm */}
      {showSerialConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-60">
          <div className="bg-white rounded-lg p-6 max-w-sm mx-4">
            <h3 className="font-semibold mb-2">Confirm Serial Change</h3>
            <p className="text-sm text-gray-600 mb-3">From: <span className="font-mono">{originalSerialNumber || '(empty)'}</span><br/>To: <span className="font-mono">{formData.serialNumber}</span></p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowSerialConfirm(false)} className="px-3 py-1.5 bg-gray-100 rounded">Cancel</button>
              <button onClick={() => handleSubmit()} className="px-3 py-1.5 bg-amber-600 text-white rounded">Confirm</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-60">
          <div className="bg-white rounded-lg p-6 max-w-sm mx-4">
            <h3 className="font-semibold mb-2">Delete {item.name}?</h3>
            <p className="text-sm text-gray-600 mb-3">This cannot be undone.</p>
            {item.itemType === 'SERIALIZED' && <p className="text-xs text-amber-700 bg-amber-50 p-2 rounded mb-3">Consider "Retired" status to maintain audit trail.</p>}
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowDeleteConfirm(false)} className="px-3 py-1.5 bg-gray-100 rounded">Cancel</button>
              <button onClick={handleDelete} disabled={deleting} className="px-3 py-1.5 bg-red-600 text-white rounded disabled:opacity-50">{deleting ? 'Deleting...' : 'Delete'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditItemModal;
